import { rulesEvaluatorManager } from '@/core/usertour-rules-evaluator';
import { timerManager } from '@/utils/timer-manager';
import { logger } from '@/utils';
import { Evented } from '@/utils/evented';
import { autoBind } from '@/utils';
import { uuidV4, isConditionsActived } from '@usertour/helpers';
import { TrackCondition } from '@usertour/types';
import { SDKClientEvents } from '@usertour/constants';

const log = logger.scope('conditions');
// === Interfaces ===
/**
 * Options for condition monitoring
 */
interface ConditionsMonitorOptions {
  /** Whether to start monitoring automatically (default: true) */
  autoStart?: boolean;
  /** Monitoring interval in milliseconds (default: 1000) */
  interval?: number;
}

/**
 * Event data for condition state change
 */
export type ConditionStateChangeEvent = {
  trackCondition: TrackCondition;
  timestamp: number;
  state: 'activated' | 'deactivated';
};

/**
 * UsertourConditionsMonitor handles dynamic conditions monitoring and reporting
 * Monitors an array of TrackCondition and reports when they become active or inactive
 */
export class UsertourConditionsMonitor extends Evented {
  // === Properties ===
  private conditions: TrackCondition[] = [];
  private activeConditions: Set<string> = new Set(); // Track currently active conditions
  private readonly id: string;
  private readonly options: ConditionsMonitorOptions;
  private intervalId: string | null = null;
  private isStarted = false; // Track if monitoring has been started

  // === Constructor ===
  constructor(options: ConditionsMonitorOptions = {}) {
    super();
    autoBind(this);

    this.id = uuidV4();
    this.options = {
      autoStart: true,
      interval: 1000,
      ...options,
    };

    // Start monitoring automatically if enabled
    if (this.options.autoStart) {
      this.start();
    }
  }

  // === Public API ===
  /**
   * Adds conditions to monitor
   * @param conditions - Array of TrackCondition objects to add
   */
  addConditions(conditions: TrackCondition[]): void {
    for (const trackCondition of conditions) {
      // Remove existing condition with same ID if it exists
      const existingIndex = this.conditions.findIndex(
        (c) => c.condition.id === trackCondition.condition.id,
      );
      if (existingIndex !== -1) {
        this.conditions.splice(existingIndex, 1);
        // Also remove from active conditions to prevent memory leaks
        this.activeConditions.delete(trackCondition.condition.id);
      }

      // Add new condition
      this.conditions.push(trackCondition);
    }

    // Immediately check the initial state of newly added conditions
    if (conditions.length > 0) {
      this.checkInitialConditionStates(conditions);
    }
  }

  /**
   * Removes conditions from monitoring by their IDs
   * @param conditionIds - Array of condition IDs to remove
   */
  removeConditions(conditionIds: string[]): void {
    this.conditions = this.conditions.filter(
      (trackCondition) => !conditionIds.includes(trackCondition.condition.id),
    );
    // Also remove from active conditions to prevent memory leaks
    for (const id of conditionIds) {
      this.activeConditions.delete(id);
    }
  }

  /**
   * Clears all conditions
   */
  clearConditions(): void {
    this.conditions = [];
    this.activeConditions.clear();
  }

  /**
   * Gets current conditions
   */
  getConditions(): TrackCondition[] {
    return [...this.conditions];
  }

  /**
   * Gets set of active condition IDs
   */
  getActiveConditionIds(): Set<string> {
    return new Set(this.activeConditions);
  }

  /**
   * Gets monitoring statistics
   */
  getStats(): {
    totalConditions: number;
    activeConditions: number;
    isMonitoring: boolean;
  } {
    return {
      totalConditions: this.conditions.length,
      activeConditions: this.activeConditions.size,
      isMonitoring: this.isStarted,
    };
  }

  // === Monitoring Control ===
  /**
   * Starts monitoring
   * When started, begins periodic condition checking
   */
  start(): void {
    if (this.isStarted) {
      return; // Already started
    }

    this.isStarted = true;
    this.intervalId = `${this.id}-monitor`;
    timerManager.setInterval(
      this.intervalId,
      async () => {
        await this.checkConditions();
      },
      this.options.interval || 1000,
    );

    // Check initial states of all existing conditions when starting
    if (this.conditions.length > 0) {
      this.checkInitialConditionStates(this.conditions);
    }
  }

  /**
   * Stops monitoring
   */
  stop(): void {
    if (this.intervalId) {
      timerManager.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isStarted = false;
  }

  // === Condition Checking ===
  /**
   * Evaluates a single condition using the appropriate evaluator
   * @param trackCondition - The condition to evaluate
   * @returns Whether the condition is active (returns false on error)
   */
  private async evaluateCondition(trackCondition: TrackCondition): Promise<boolean> {
    try {
      const evaluator = rulesEvaluatorManager.getEvaluator(trackCondition.contentId);
      const [evaluatedCondition] = await evaluator.evaluate([trackCondition.condition]);
      return isConditionsActived([evaluatedCondition]);
    } catch (error) {
      log.error(`Failed to evaluate the condition (${trackCondition.condition.id})`, error);
      return false;
    }
  }

  /**
   * Checks the initial state of newly added conditions
   * @param conditions - Array of TrackCondition objects to check
   */
  private async checkInitialConditionStates(conditions: TrackCondition[]): Promise<void> {
    // Skip check if monitoring hasn't started yet - prevents false negatives for DOM-dependent conditions
    // (e.g., UNPRESENT returning true when element just hasn't loaded yet)
    // The periodic checkConditions() will handle it once monitoring starts
    if (conditions.length === 0 || !this.isStarted) {
      return;
    }

    try {
      for (const trackCondition of conditions) {
        const isActive = await this.evaluateCondition(trackCondition);

        if (isActive) {
          // Condition is currently active
          if (!this.activeConditions.has(trackCondition.condition.id)) {
            this.activeConditions.add(trackCondition.condition.id);
            await this.reportConditionStateChange(trackCondition, 'activated');
          }
        } else {
          // Condition is currently inactive - report deactivated for initial state
          if (!this.activeConditions.has(trackCondition.condition.id)) {
            await this.reportConditionStateChange(trackCondition, 'deactivated');
          }
        }
      }
    } catch (error) {
      log.error('Failed to check the initial condition states', error);
    }
  }

  /**
   * Checks all conditions and reports active ones
   */
  private async checkConditions(): Promise<void> {
    // Skip check if monitoring hasn't started
    if (this.conditions.length === 0 || !this.isStarted) {
      return;
    }

    try {
      const currentActiveConditionIds = new Set<string>();

      for (const trackCondition of this.conditions) {
        const isActive = await this.evaluateCondition(trackCondition);

        if (isActive) {
          currentActiveConditionIds.add(trackCondition.condition.id);

          // Check if condition just became active
          if (!this.activeConditions.has(trackCondition.condition.id)) {
            this.activeConditions.add(trackCondition.condition.id);
            await this.reportConditionStateChange(trackCondition, 'activated');
          }
        }
      }

      // Check for conditions that became inactive
      const deactivatedIds = [...this.activeConditions].filter(
        (id) => !currentActiveConditionIds.has(id),
      );

      for (const activeConditionId of deactivatedIds) {
        const trackCondition = this.conditions.find((tc) => tc.condition.id === activeConditionId);
        if (trackCondition) {
          this.activeConditions.delete(activeConditionId);
          await this.reportConditionStateChange(trackCondition, 'deactivated');
        }
      }
    } catch (error) {
      log.error('Failed to check conditions', error);
    }
  }

  // === Event Reporting ===
  /**
   * Reports a condition state change
   * This method can be overridden or extended for custom reporting logic
   */
  protected async reportConditionStateChange(
    trackCondition: TrackCondition,
    state: 'activated' | 'deactivated',
  ): Promise<void> {
    try {
      const eventData: ConditionStateChangeEvent = {
        trackCondition,
        timestamp: Date.now(),
        state,
      };

      // Emit event for external listeners
      this.trigger(SDKClientEvents.CONDITION_STATE_CHANGED, eventData);

      // Log for debugging
      log.debug(`Condition ${state}`, eventData);
    } catch (error) {
      log.error(`Failed to report the condition state (${state})`, error);
    }
  }

  // === Cleanup ===
  /**
   * Cleans up the monitor and cleans up resources
   */
  cleanup(): void {
    this.stop();
    this.clearConditions();
  }
}
